/**
 * <s-infinite-scroll data-next-url="…" data-target="#product-grid"> — loads the next
 * page of a paginated grid when the shopper scrolls near the end.
 *
 * Required child markup: one [data-infinite-scroll-link] anchor pointing at the next
 * page. It is the no-JavaScript fallback and a manual "Load more" button; while
 * JavaScript runs it is observed and fetched automatically. Each fetched page must
 * render the same target grid and its own <s-infinite-scroll> with the following URL.
 */
class InfiniteScroll extends HTMLElement {
  #observer = null;
  #loading = false;
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const link = this.querySelector('[data-infinite-scroll-link]');
    if (!link) return;

    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.#load();
    }, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#load();
    }, { rootMargin: '600px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  async #load() {
    const nextUrl = this.dataset.nextUrl;
    const target = document.querySelector(this.dataset.target || '#product-grid');
    if (this.#loading || !nextUrl || !target) return;

    this.#loading = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(nextUrl, { signal: this.#ac.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.querySelector(this.dataset.target || '#product-grid');
      if (nextGrid) target.append(...Array.from(nextGrid.children));

      const nextLoader = doc.querySelector('s-infinite-scroll');
      const following = nextLoader?.dataset.nextUrl;
      if (following) {
        this.dataset.nextUrl = following;
        const link = this.querySelector('[data-infinite-scroll-link]');
        if (link) link.href = following;
      } else {
        this.remove();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Leave the visible link in place so the shopper can retry or navigate natively.
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-infinite-scroll')) {
  customElements.define('s-infinite-scroll', InfiniteScroll);
}
